// src/components/admin/blog/TagManager.jsx
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FiPlus,
  FiX,
  FiTag,
  FiClipboard,
  FiHash,
  FiAlertCircle,
  FiInfo,
} from 'react-icons/fi';

// Tooltip Component with React Portal - Fixed flickering
const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const showTimeoutRef = useRef(null);

  const showTooltip = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    // Small delay to prevent flickering
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      updatePosition();
      showTimeoutRef.current = null;
    }, 50);
  };

  const hideTooltip = () => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Delay hiding to allow moving to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      hideTimeoutRef.current = null;
    }, 150);
  };

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 280;
      const tooltipHeight = 150;

      let top, left;

      switch (position) {
        case 'top':
          top = rect.top - tooltipHeight - 10;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'bottom':
          top = rect.bottom + 10;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - 10;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + 10;
          break;
        default:
          top = rect.top - tooltipHeight - 10;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
      }

      // Ensure tooltip stays within viewport
      const padding = 10;
      if (top < padding) top = padding;
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }
      if (top + tooltipHeight > window.innerHeight - padding) {
        top = window.innerHeight - tooltipHeight - padding;
      }

      setTooltipPosition({ top, left });
    }
  };

  // Handle tooltip hover to keep it open
  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    hideTooltip();
  };

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, []);

  const getArrowPosition = pos => {
    switch (pos) {
      case 'top':
        return {
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
        };
      case 'bottom':
        return {
          top: '-6px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
        };
      case 'left':
        return {
          right: '-6px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
        };
      case 'right':
        return {
          left: '-6px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
        };
      default:
        return {
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
        };
    }
  };

  const tooltipContent =
    isVisible &&
    ReactDOM.createPortal(
      <div
        className="fixed z-[9999] animate-fade-in-up"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: '280px',
        }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        <div
          className="px-4 py-3 rounded-lg shadow-xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
            color: 'var(--text)',
          }}
        >
          {content}
          <div
            className="absolute w-2 h-2 rotate-45 border-r border-b"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
              ...getArrowPosition(position),
            }}
          />
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="cursor-help inline-flex"
      >
        {children}
      </div>
      {tooltipContent}
    </>
  );
};

// Main TagManager Component
export const TagManager = ({
  selectedTags,
  onTagsChange,
  existingTags = [],
  placeholder = 'Type or paste tags...',
  className = '',
  disabled = false,
  showCount = true,
  showActions = true,
  variant = 'default', // 'default', 'compact', 'minimal'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showParseWarning, setShowParseWarning] = useState(false);
  const [parseMessage, setParseMessage] = useState('');
  const inputRef = useRef(null);

  /**
   * Parse tags from various input formats
   * Supports: comma-separated, space-separated, hashtags, mixed formats
   */
  const parseTagsFromInput = input => {
    if (!input || !input.trim()) return [];

    let tags = [];
    const trimmedInput = input.trim();

    // Check if input contains commas (comma-separated format)
    if (trimmedInput.includes(',')) {
      // Split by comma
      tags = trimmedInput.split(',').map(tag => tag.trim());
    }
    // Check if input contains spaces (space-separated format)
    else if (trimmedInput.includes(' ')) {
      // Split by space
      tags = trimmedInput.split(/\s+/).map(tag => tag.trim());
    }
    // Single tag
    else {
      tags = [trimmedInput];
    }

    // Process each tag
    const processedTags = [];
    for (let tag of tags) {
      if (!tag) continue;

      // Remove hashtag symbols (#) from beginning
      tag = tag.replace(/^#+/, '');

      // Remove special characters but keep hyphens and underscores
      tag = tag.toLowerCase().replace(/[^\w\u4e00-\u9fff-]/g, '');

      // Trim and ensure not empty
      tag = tag.trim();

      if (tag && tag.length > 0 && tag.length <= 50) {
        processedTags.push(tag);
      }
    }

    return [...new Set(processedTags)]; // Remove duplicates
  };

  /**
   * Handle paste event - automatically parse and add tags
   */
  const handlePaste = e => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (pastedText) {
      const parsedTags = parseTagsFromInput(pastedText);

      if (parsedTags.length > 0) {
        const newTags = [...selectedTags];
        let addedCount = 0;

        for (const tag of parsedTags) {
          if (!newTags.includes(tag)) {
            newTags.push(tag);
            addedCount++;
          }
        }

        if (addedCount > 0) {
          onTagsChange(newTags);
          setParseMessage(`${addedCount} tag(s) added from clipboard`);
          setShowParseWarning(true);
          setTimeout(() => setShowParseWarning(false), 3000);
        } else {
          setParseMessage('No new tags found in clipboard content');
          setShowParseWarning(true);
          setTimeout(() => setShowParseWarning(false), 3000);
        }
      } else {
        setParseMessage('No valid tags found in clipboard content');
        setShowParseWarning(true);
        setTimeout(() => setShowParseWarning(false), 3000);
      }
    }

    setInputValue('');
  };

  /**
   * Handle input change with real-time parsing
   */
  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    // Show suggestions for existing tags
    if (value && existingTags && existingTags.length > 0) {
      const matches = existingTags
        .filter(tag => tag.toLowerCase().includes(value.toLowerCase()))
        .filter(tag => !selectedTags.includes(tag))
        .slice(0, 5);
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }

    // Auto-parse when user types certain delimiters
    if (value.includes(',') || value.includes(' ') || value.includes('#')) {
      const parsedTags = parseTagsFromInput(value);
      if (parsedTags.length > 0) {
        const newTags = [...selectedTags];
        let addedCount = 0;

        for (const tag of parsedTags) {
          if (!newTags.includes(tag)) {
            newTags.push(tag);
            addedCount++;
          }
        }

        if (addedCount > 0) {
          onTagsChange(newTags);
          setInputValue('');
          setSuggestions([]);
          setParseMessage(`${addedCount} tag(s) added`);
          setShowParseWarning(true);
          setTimeout(() => setShowParseWarning(false), 2000);
        }
      }
    }
  };

  /**
   * Handle key press events
   */
  const handleKeyPress = e => {
    if (disabled) return;

    // Enter key - add current input as tag
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      handleAddTag(inputValue);
    }
    // Comma key - add current input as tag
    else if (e.key === ',' && inputValue) {
      e.preventDefault();
      handleAddTag(inputValue);
    }
    // Space key - add current input as tag
    else if (e.key === ' ' && inputValue && !inputValue.includes(' ')) {
      e.preventDefault();
      handleAddTag(inputValue);
    }
    // Backspace - remove last tag if input is empty
    else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      handleRemoveTag(selectedTags[selectedTags.length - 1]);
    }
  };

  /**
   * Add a single tag
   */
  const handleAddTag = tag => {
    if (disabled) return;

    const trimmedTag = tag.trim().toLowerCase();

    if (!trimmedTag) return;

    // Validate tag
    if (trimmedTag.length > 50) {
      setParseMessage('Tag cannot exceed 50 characters');
      setShowParseWarning(true);
      setTimeout(() => setShowParseWarning(false), 2000);
      return;
    }

    // Remove hashtags and special characters
    const cleanTag = trimmedTag
      .replace(/^#+/, '')
      .replace(/[^\w\u4e00-\u9fff-]/g, '');

    if (cleanTag && !selectedTags.includes(cleanTag)) {
      onTagsChange([...selectedTags, cleanTag]);
    }

    setInputValue('');
    setSuggestions([]);
  };

  /**
   * Remove a tag
   */
  const handleRemoveTag = tagToRemove => {
    if (disabled) return;
    onTagsChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  /**
   * Add all suggested tags at once
   */
  const handleAddAllSuggestions = () => {
    if (disabled) return;

    const newTags = [...selectedTags];
    let addedCount = 0;

    for (const tag of suggestions) {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
        addedCount++;
      }
    }

    if (addedCount > 0) {
      onTagsChange(newTags);
      setSuggestions([]);
      setParseMessage(`${addedCount} suggestion(s) added`);
      setShowParseWarning(true);
      setTimeout(() => setShowParseWarning(false), 2000);
    }
  };

  /**
   * Bulk add tags from a text block
   */
  const handleBulkAdd = () => {
    if (disabled) return;

    const text = prompt(
      'Enter tags separated by commas, spaces, or hashtags:\n\nExample: design, development, react\nExample: #design #development #react',
    );
    if (text) {
      const parsedTags = parseTagsFromInput(text);
      if (parsedTags.length > 0) {
        const newTags = [...selectedTags];
        let addedCount = 0;

        for (const tag of parsedTags) {
          if (!newTags.includes(tag)) {
            newTags.push(tag);
            addedCount++;
          }
        }

        if (addedCount > 0) {
          onTagsChange(newTags);
          setParseMessage(`${addedCount} tag(s) added from bulk input`);
          setShowParseWarning(true);
          setTimeout(() => setShowParseWarning(false), 3000);
        } else {
          setParseMessage('No new tags found in bulk input');
          setShowParseWarning(true);
          setTimeout(() => setShowParseWarning(false), 2000);
        }
      }
    }
  };

  /**
   * Copy all tags to clipboard
   */
  const handleCopyTags = () => {
    if (selectedTags.length === 0 || disabled) return;

    const tagsString = selectedTags.map(tag => `#${tag}`).join(' ');
    navigator.clipboard.writeText(tagsString);
    setParseMessage('Tags copied to clipboard');
    setShowParseWarning(true);
    setTimeout(() => setShowParseWarning(false), 2000);
  };

  // Determine CSS classes based on variant
  const getVariantClasses = () => {
    switch (variant) {
      case 'compact':
        return 'space-y-2';
      case 'minimal':
        return 'space-y-1';
      default:
        return 'space-y-3';
    }
  };

  const getTagDisplayClasses = () => {
    switch (variant) {
      case 'compact':
        return 'min-h-[32px] p-1.5';
      case 'minimal':
        return 'min-h-[28px] p-1';
      default:
        return 'min-h-[40px] p-2';
    }
  };

  const getTagClasses = () => {
    switch (variant) {
      case 'compact':
        return 'px-2 py-0.5 text-xs';
      case 'minimal':
        return 'px-1.5 py-0.5 text-xs';
      default:
        return 'px-2.5 py-1 text-sm';
    }
  };

  return (
    <div className={`${getVariantClasses()} ${className}`}>
      {/* Header with Title and Info Tooltip */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label
            className="block text-sm font-medium"
            style={{ color: 'var(--text)' }}
          >
            Tags
          </label>
          <Tooltip
            position="top"
            content={
              <div className="space-y-1 text-left">
                <p className="font-medium mb-1">✨ Quick Tips:</p>
                <p>• Paste comma-separated: design, dev, react</p>
                <p>• Paste space-separated: design dev react</p>
                <p>• Use hashtags: #design #dev #react</p>
                <p>• Press Enter/Space/Comma to add</p>
                <p>• Press Backspace to remove last tag</p>
              </div>
            }
          >
            <FiInfo
              className="w-4 h-4 cursor-help"
              style={{ color: 'var(--muted)' }}
            />
          </Tooltip>
        </div>
        {showCount && (
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Tags Display Area */}
      <div
        className={`flex flex-wrap gap-2 rounded-lg border ${getTagDisplayClasses()}`}
        style={{ borderColor: 'var(--border)' }}
      >
        {selectedTags.length === 0 ? (
          <span className="text-sm" style={{ color: 'var(--muted)' }}>
            No tags added yet
          </span>
        ) : (
          selectedTags.map(tag => (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 rounded-full transition-all duration-200 hover:shadow-sm group ${getTagClasses()}`}
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              <FiTag className="w-3 h-3" />
              <span>{tag}</span>
              {!disabled && (
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 opacity-60 hover:opacity-100 transition-opacity"
                  title="Remove tag"
                >
                  <FiX className="w-3 h-3" />
                </button>
              )}
            </span>
          ))
        )}
      </div>

      {/* Input Area */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <FiTag
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--muted)' }}
            />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full pl-9 pr-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
          </div>
          <button
            onClick={() => inputValue && handleAddTag(inputValue)}
            disabled={!inputValue || disabled}
            className="px-3 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiPlus className="w-4 h-4" />
          </button>
        </div>

        {/* Suggestions Dropdown */}
        {suggestions.length > 0 && (
          <div
            className="absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg overflow-hidden z-10 animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div
              className="flex items-center justify-between px-3 py-2 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--muted)' }}
              >
                Suggestions
              </span>
              <button
                onClick={handleAddAllSuggestions}
                className="text-xs hover:underline"
                style={{ color: 'var(--text)' }}
              >
                Add all
              </button>
            </div>
            {suggestions.map(suggestion => (
              <button
                key={suggestion}
                onClick={() => handleAddTag(suggestion)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <FiHash className="w-3 h-3" style={{ color: 'var(--muted)' }} />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Parse Warning Message */}
      {showParseWarning && (
        <div
          className="flex items-center gap-2 p-2 rounded-lg animate-fade-in-up"
          style={{ backgroundColor: 'var(--border-light)' }}
        >
          <FiAlertCircle className="w-4 h-4 text-blue-500" />
          <span className="text-xs" style={{ color: 'var(--text)' }}>
            {parseMessage}
          </span>
        </div>
      )}

      {/* Action Buttons */}
      {showActions && !disabled && (
        <div
          className="flex flex-wrap gap-2 pt-2 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <button
            onClick={handleBulkAdd}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: 'var(--muted)' }}
          >
            <FiClipboard className="w-3 h-3" />
            Bulk Add
          </button>
          {selectedTags.length > 0 && (
            <>
              <button
                onClick={handleCopyTags}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                style={{ color: 'var(--muted)' }}
              >
                <FiClipboard className="w-3 h-3" />
                Copy All
              </button>
              <button
                onClick={() => onTagsChange([])}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                style={{ color: '#ef4444' }}
              >
                <FiX className="w-3 h-3" />
                Clear All
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
